(function () {
  if (window.OeIndependentFilterDataAdapter) return;

  class IndependentFilterDataAdapter {
    constructor(index) {
      if (!index || !Array.isArray(index.products) || !index.dimensions) {
        throw new Error('Ungültiger unabhängiger Filterindex.');
      }
      this.index = index;
      this.products = index.products;
      this.dimensions = index.dimensions;
      this.rankCache = new Map();
      this.dimensionEntries = Object.entries(this.dimensions);
    }

    getProfile() {
      return {
        key: this.index.profile,
        version: this.index.version,
        collection: this.index.collection,
        generatedAt: this.index.generatedAt,
      };
    }

    getProducts() {
      return this.products;
    }

    getVariants(productId) {
      return this.products.find((product) => product.id === String(productId))?.v || [];
    }

    getFilterValues(handle) {
      return this.index.values[handle];
    }

    getPriceBounds() {
      return this.getNumericBounds('preis');
    }

    getNumericBounds(handle) {
      const values = this.index.values[handle];
      return values && !Array.isArray(values) ? values : null;
    }

    hasConstraint(state, handle) {
      return Boolean(state.values?.[handle]?.length || state.ranges?.[handle]);
    }

    hasAnyConstraint(state) {
      return Object.keys(state.values || {}).length > 0 || Object.keys(state.ranges || {}).length > 0;
    }

    cloneState(state) {
      return {
        values: Object.fromEntries(
          Object.entries(state.values || {}).map(([handle, values]) => [handle, [...values]]),
        ),
        ranges: Object.fromEntries(
          Object.entries(state.ranges || {}).map(([handle, range]) => [handle, { ...range }]),
        ),
      };
    }

    valueMatches(candidate, selected) {
      const values = Array.isArray(candidate) ? candidate : [candidate];
      return selected.some((value) => values.includes(value));
    }

    numberMatches(candidate, range) {
      if (Array.isArray(candidate)) return candidate.some((value) => this.numberMatches(value, range));
      const value = Number(candidate);
      if (!Number.isFinite(value)) return false;
      if (range.min != null && value < range.min) return false;
      if (range.max != null && value > range.max) return false;
      return true;
    }

    entityMatches(entity, state, scope) {
      for (const [handle, dimension] of Object.entries(this.dimensions)) {
        if (dimension.s !== scope || !this.hasConstraint(state, handle)) continue;
        const candidate = entity[dimension.k];
        if (dimension.t === 'number') {
          if (!this.numberMatches(candidate, state.ranges[handle])) return false;
        } else if (!this.valueMatches(candidate, state.values[handle])) return false;
      }
      return true;
    }

    matchingVariants(product, state) {
      if (this.index.profile === 'PFLANZKUEBEL') {
        const constraints = this.constraints(state, 'v');
        return product.v.filter((variant) => variant.a === 1 && this.matchesConstraints(variant, constraints));
      }
      return product.v.filter(
        (variant) => variant.a === 1 && this.entityMatches(variant, state, 'v'),
      );
    }

    productMatches(product, state) {
      if (!this.hasAnyConstraint(state)) return true;
      if (this.index.profile === 'PFLANZKUEBEL') {
        return this.matchesConstraints(product.p, this.constraints(state, 'p')) &&
          this.hasMatchingVariant(product, this.constraints(state, 'v'));
      }
      return this.entityMatches(product.p, state, 'p') && this.matchingVariants(product, state).length > 0;
    }

    applyState(state) {
      if (this.index.profile === 'PFLANZKUEBEL') {
        if (!this.hasAnyConstraint(state)) return [...this.products];
        const products = this.constraints(state, 'p');
        const variants = this.constraints(state, 'v');
        return this.products.filter((product) => this.matchesConstraints(product.p, products) &&
          this.hasMatchingVariant(product, variants));
      }
      return this.products.filter((product) => this.productMatches(product, state));
    }

    // Compile only active dimensions once per operation, never inside a variant loop.
    constraints(state, scope, except) {
      return this.dimensionEntries
        .filter(([handle, dimension]) => handle !== except && dimension.s === scope && this.hasConstraint(state, handle))
        .map(([handle, dimension]) => [dimension.k, dimension.t, dimension.t === 'number' ? state.ranges[handle] : state.values[handle]]);
    }

    matchesConstraints(entity, constraints) {
      for (const [key, type, selected] of constraints) {
        if (type === 'number') {
          if (!this.numberMatches(entity[key], selected)) return false;
        } else if (!this.valueMatches(entity[key], selected)) return false;
      }
      return true;
    }

    hasMatchingVariant(product, constraints) {
      return product.v.some((variant) => variant.a === 1 && this.matchesConstraints(variant, constraints));
    }

    // Disjunctive facet counts: replace this facet, retain every other constraint.
    // A product contributes at most once to each value, witnessed by a real variant.
    facetCounts(state) {
      const counts = {};
      const currentMatches = this.applyState(state).length;
      for (const [handle, dimension] of this.dimensionEntries) {
        const values = this.getFilterValues(handle);
        if (dimension.t === 'number') {
          counts[handle] = { ...values, matches: currentMatches };
          continue;
        }
        const result = counts[handle] = Object.fromEntries(values.map((value) => [value, 0]));
        const allowed = new Set(values);
        const productConstraints = this.constraints(state, 'p', handle);
        const variantConstraints = this.constraints(state, 'v', handle);
        for (const product of this.products) {
          if (!this.matchesConstraints(product.p, productConstraints)) continue;
          const found = new Set();
          if (dimension.s === 'p') {
            if (!this.hasMatchingVariant(product, variantConstraints)) continue;
            const candidate = product.p[dimension.k];
            for (const value of Array.isArray(candidate) ? candidate : [candidate]) {
              if (allowed.has(value)) found.add(value);
            }
          } else {
            for (const variant of product.v) {
              if (variant.a !== 1 || !this.matchesConstraints(variant, variantConstraints)) continue;
              const candidate = variant[dimension.k];
              for (const value of Array.isArray(candidate) ? candidate : [candidate]) {
                if (allowed.has(value)) found.add(value);
              }
              if (found.size === allowed.size) break;
            }
          }
          for (const value of found) result[value] += 1;
        }
      }
      return counts;
    }

    getCounts(state) {
      if (this.index.profile === 'PFLANZKUEBEL') return this.facetCounts(state);
      const counts = {};
      const currentMatches = this.applyState(state).length;
      for (const [handle, dimension] of Object.entries(this.dimensions)) {
        const filterValues = this.getFilterValues(handle);
        if (dimension.t === 'number') {
          counts[handle] = {
            ...filterValues,
            matches: currentMatches,
          };
          continue;
        }
        counts[handle] = {};
        for (const value of filterValues) {
          const candidateState = this.cloneState(state);
          candidateState.values[handle] = [value];
          delete candidateState.ranges[handle];
          counts[handle][value] = this.applyState(candidateState).length;
        }
      }
      return counts;
    }

    matchingPrice(product, state) {
      let variants = this.matchingVariants(product, state);
      if (!variants.length && !this.hasAnyConstraint(state)) variants = product.v;
      const prices = variants.map((variant) => variant.pr);
      return prices.length ? Math.min(...prices) : Number.POSITIVE_INFINITY;
    }

    rankMap(sortKey) {
      const normalized = ['manual', 'best-selling', 'most-relevant'].includes(sortKey)
        ? sortKey
        : 'manual';
      if (!this.rankCache.has(normalized)) {
        this.rankCache.set(
          normalized,
          new Map((this.index.sortRanks[normalized] || []).map((handle, index) => [handle, index])),
        );
      }
      return this.rankCache.get(normalized);
    }

    sortProducts(products, state, sortKey) {
      const sorted = [...products];
      const byTitle = (left, right) => left.t.localeCompare(right.t, 'de', { numeric: true });
      if (sortKey === 'title-ascending') return sorted.sort(byTitle);
      if (sortKey === 'title-descending') return sorted.sort((left, right) => byTitle(right, left));
      if (sortKey === 'created-ascending') {
        return sorted.sort((left, right) => left.cr.localeCompare(right.cr) || byTitle(left, right));
      }
      if (sortKey === 'created-descending') {
        return sorted.sort((left, right) => right.cr.localeCompare(left.cr) || byTitle(left, right));
      }
      if (sortKey === 'price-ascending' || sortKey === 'price-descending') {
        const direction = sortKey === 'price-descending' ? -1 : 1;
        return sorted.sort(
          (left, right) =>
            (this.matchingPrice(left, state) - this.matchingPrice(right, state)) * direction ||
            byTitle(left, right),
        );
      }
      const rank = this.rankMap(sortKey);
      return sorted.sort(
        (left, right) =>
          (rank.get(left.h) ?? Number.MAX_SAFE_INTEGER) -
            (rank.get(right.h) ?? Number.MAX_SAFE_INTEGER) || byTitle(left, right),
      );
    }

    selectVariant(product, state) {
      let variants = this.matchingVariants(product, state);
      if (!variants.length && !this.hasAnyConstraint(state)) variants = [...product.v];
      return variants.sort(
        (left, right) => left.pr - right.pr || Number(left.id) - Number(right.id),
      )[0];
    }
  }

  window.OeIndependentFilterDataAdapter = IndependentFilterDataAdapter;
})();
